import React, { useMemo, useState } from 'react';
import Table from '../../components/Table';
import Button from '../../components/Button';
import PlusIcon from 'assets/images/icon/plus-white.png'
import Input from '../../components/Input';
import search from 'assets/images/icon/search-normal.png';
import ToggleSwitch from '../../components/ToggleSwitch';
import Card from '../../components/Card';
import { useNavigate } from 'react-router-dom';
import routes from 'constants/routes';
import styles from "./deductions.module.scss";
import { cn, stringMerger } from 'utils';
import CompanyLogo from 'assets/images/icon/company-logo';
import UsersLogo from 'assets/images/icon/users-logo';
import { getMcnDeductColumns, getChildSupportDeductColumns } from './columns';
import DollarIcon from 'assets/images/icon/dollar-icon';
import RupeesIcon from 'assets/images/icon/rupees-icon';
import RupeesPocket from 'assets/images/icon/rupees-pocket';
import HandshakeIcon from 'assets/images/icon/handshake-icon';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getMiscDeductions, getChildSupportDeductions, updateMiscDeduction, updateChildSupportDeduction } from 'services/deductions';
import { getEmployees } from 'services/employees';
import ConfirmModal from 'components/ConfirmModal';

function Deductions() {
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const [selectedTab, setSelectedTab] = useState(0);
    const [searchTerm, setSearchTerm] = useState('');
    const [showInactive, setShowInactive] = useState(false);
    const [viewDeduction, setViewDeduction] = useState(null);
    const [confirmModal, setConfirmModal] = useState(null);

    const { mutate: mutateMisc } = useMutation({
        mutationFn: updateMiscDeduction,
        onSuccess: () => queryClient.invalidateQueries({ queryKey: ['misc-deductions'] }),
    });

    const { mutate: mutateChildSupport } = useMutation({
        mutationFn: updateChildSupportDeduction,
        onSuccess: () => queryClient.invalidateQueries({ queryKey: ['child-support-deductions'] }),
    });

    const handleToggleActive = (d) => {
        const rawItem = selectedTab === 0
            ? miscRaw.find(r => r.id === d.id)
            : childSupportRaw.find(r => r.id === d.id);
        if (!rawItem) return;
        const isDeactivating = rawItem.active !== false;
        const label = selectedTab === 0 ? 'Miscellaneous Deduction' : 'Child Support Deduction';
        setConfirmModal({
            title: isDeactivating ? `Deactivate ${label}` : `Activate ${label}`,
            message: isDeactivating
                ? 'This deduction will be marked inactive and will no longer be applied in future payroll runs.'
                : 'This deduction will be re-activated and applied in future payroll runs.',
            confirmLabel: isDeactivating ? 'Deactivate' : 'Activate',
            confirmVariant: isDeactivating ? 'danger' : 'success',
            onConfirm: () => {
                if (selectedTab === 0) mutateMisc({ deductionId: d.id, active: !isDeactivating });
                else mutateChildSupport({ deductionId: d.id, active: !isDeactivating });
                setConfirmModal(null);
            },
        });
    };

    const { data: miscRawApi, isLoading: loadingMisc } = useQuery({
        queryKey: ['misc-deductions'],
        queryFn: getMiscDeductions,
    });

    const { data: childSupportRawApi, isLoading: loadingChildSupport } = useQuery({
        queryKey: ['child-support-deductions'],
        queryFn: getChildSupportDeductions,
    });

    const { data: employeesRawApi } = useQuery({
        queryKey: ['employees'],
        queryFn: getEmployees,
    });

    const miscRaw = useMemo(() => Array.isArray(miscRawApi) ? miscRawApi : (miscRawApi?.data ?? []), [miscRawApi]);
    const childSupportRaw = useMemo(() => Array.isArray(childSupportRawApi) ? childSupportRawApi : (childSupportRawApi?.data ?? []), [childSupportRawApi]);
    const employeesRaw = useMemo(() => Array.isArray(employeesRawApi) ? employeesRawApi : (employeesRawApi?.data ?? []), [employeesRawApi]);

    const employeeMap = useMemo(() => {
        const map = {};
        employeesRaw.forEach(e => {
            map[e.id] = [e.firstName, e.lastName].filter(Boolean).join(' ') || e.id;
        });
        return map;
    }, [employeesRaw]);

    const mcnDeductionsData = useMemo(() => miscRaw.map(d => ({
        id: d.id,
        employee: employeeMap[d.employeeId] || d.employeeId || '—',
        type: 'Miscellaneous',
        start_date: d.effectiveStart ? new Date(d.effectiveStart).toLocaleDateString('en-US', { timeZone: 'UTC' }) : (d.effective ? new Date(d.effective).toLocaleDateString('en-US', { timeZone: 'UTC' }) : '—'),
        end_date: d.effectiveEnd ? new Date(d.effectiveEnd).toLocaleDateString('en-US', { timeZone: 'UTC' }) : '—',
        deduction: d.amount != null ? `$${d.amount}` : '—',
        deduction_gap: d.annualLimitAmount != null ? `$${d.annualLimitAmount}` : (d.annual != null ? `$${d.annual}` : '—'),
        description: d.description || '—',
    })), [miscRaw, employeeMap]);

    const childSupportDeductionsData = useMemo(() => childSupportRaw.map(d => ({
        id: d.id,
        employee: employeeMap[d.employeeId] || d.employeeId || '—',
        type: 'Child Support',
        start_date: d.effectiveStart ? new Date(d.effectiveStart).toLocaleDateString('en-US', { timeZone: 'UTC' }) : (d.effective ? new Date(d.effective).toLocaleDateString('en-US', { timeZone: 'UTC' }) : '—'),
        end_date: d.effectiveEnd ? new Date(d.effectiveEnd).toLocaleDateString('en-US', { timeZone: 'UTC' }) : '—',
        deduction: d.amount != null ? `$${d.amount}` : '—',
        max_percent: d.maxPercent != null ? `${d.maxPercent}%` : '—',
        description: d.description || '—',
    })), [childSupportRaw, employeeMap]);

    const activeMisc = miscRaw.filter(d => d.active).length;
    const activeChildSupport = childSupportRaw.filter(d => d.active).length;

    const GARNISHMENT_PREFIX = '[Garnishment]';
    const garnishmentRaw = miscRaw.filter(d => (d.description || '').startsWith(GARNISHMENT_PREFIX));
    const activeGarnishments = garnishmentRaw.filter(d => d.active).length;

    const companyCards = [
        { title: 'Miscellaneous Deductions', count: miscRaw.filter(d => !(d.description || '').startsWith(GARNISHMENT_PREFIX)).length, icon: <DollarIcon />,    tab: 0    },
        { title: 'Child Support Deductions', count: childSupportRaw.length,                                                            icon: <RupeesIcon />,    tab: 1    },
        { title: 'Wage Garnishments',        count: garnishmentRaw.length,                                                             icon: <RupeesPocket />,  tab: 2    },
        { title: 'Active Garnishments',      count: activeGarnishments,                                                                icon: <HandshakeIcon />, tab: null },
    ];

    const mcnDeductColumns = useMemo(() => getMcnDeductColumns(), []);
    const childSupportDeductColumns = useMemo(() => getChildSupportDeductColumns(), []);

    const isLoading = selectedTab === 0 ? loadingMisc : loadingChildSupport;

    const filteredMcn = useMemo(() =>
        mcnDeductionsData.filter(d => {
            const rawItem = miscRaw.find(r => r.id === d.id);
            const isActive = rawItem?.active !== false;
            if (showInactive ? isActive : !isActive) return false;
            return !searchTerm ||
                (d.employee && d.employee.toLowerCase().includes(searchTerm.toLowerCase())) ||
                (d.description && d.description.toLowerCase().includes(searchTerm.toLowerCase()));
        }), [mcnDeductionsData, miscRaw, searchTerm, showInactive]);

    const filteredChildSupport = useMemo(() =>
        childSupportDeductionsData.filter(d => {
            const rawItem = childSupportRaw.find(r => r.id === d.id);
            const isActive = rawItem?.active !== false;
            if (showInactive ? isActive : !isActive) return false;
            return !searchTerm ||
                (d.employee && d.employee.toLowerCase().includes(searchTerm.toLowerCase())) ||
                (d.description && d.description.toLowerCase().includes(searchTerm.toLowerCase()));
        }), [childSupportDeductionsData, childSupportRaw, searchTerm, showInactive]);

    const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-US', { timeZone: 'UTC' }) : '—';

    return (
        <>
        <div className={cn('content', styles.deductionContainer)}>
            <div>
                <div style={{ padding: "16px 24px 16px 16px" }}>
                    <div className="row mt-4">
                        {companyCards.map((item) => (
                            <Card
                                key={item.title}
                                title={item.title}
                                count={item.count}
                                icon={item.icon}
                                isImage={false}
                                isActive={item.tab !== null && selectedTab === item.tab}
                                onClick={item.tab !== null ? () => setSelectedTab(item.tab) : undefined}
                            />
                        ))}
                    </div>
                </div>
                <div className={styles.tabList}>
                    <button
                        className={cn(styles.tab, selectedTab === 0 ? styles.active : '')}
                        onClick={() => setSelectedTab(0)}
                    >
                        <CompanyLogo color={selectedTab === 0 ? "#E7730A" : "#5F5F5F"} />
                        <p>Misc Deductions</p>
                    </button>
                    <button
                        className={cn(styles.tab, selectedTab === 1 ? styles.active : '')}
                        onClick={() => setSelectedTab(1)}
                    >
                        <UsersLogo color={selectedTab === 1 ? "#E7730A" : "#5F5F5F"} />
                        <p>Child Support</p>
                    </button>
                    <button
                        className={cn(styles.tab, selectedTab === 2 ? styles.active : '')}
                        onClick={() => setSelectedTab(2)}
                    >
                        <RupeesPocket color={selectedTab === 2 ? "#E7730A" : "#5F5F5F"} />
                        <p>Wage Garnishments</p>
                    </button>
                </div>
                <div className={cn('container-spacing')}>
                    <div className='d-flex justify-content-between mt-4 mb-3' style={{ flexWrap: 'wrap', gap: 8 }}>
                        <div className='d-flex align-items-center gap-3'>
                            {selectedTab === 0 && (
                                <Button
                                    startIcon={<img src={PlusIcon} alt="add" />}
                                    onClick={() => navigate(
                                        stringMerger(routes.DEDUCTIONS.HOME, routes.DEDUCTIONS.ADD_EDIT_MCN_DEDUCTION)
                                    )}
                                >
                                    Add Deduction
                                </Button>
                            )}
                            {selectedTab === 1 && (
                                <Button
                                    startIcon={<img src={PlusIcon} alt="add" />}
                                    onClick={() => navigate(
                                        stringMerger(routes.DEDUCTIONS.HOME, routes.DEDUCTIONS.ADD_EDIT_CHILD_SUPPORT_DEDUCTION)
                                    )}
                                >
                                    Add Deduction
                                </Button>
                            )}
                            {selectedTab === 2 && (
                                <Button
                                    startIcon={<img src={PlusIcon} alt="add" />}
                                    onClick={() => navigate('/deductions/add-edit-garnishment-deduction')}
                                >
                                    Add Garnishment
                                </Button>
                            )}
                            <ToggleSwitch checked={showInactive} onChange={setShowInactive} />
                        </div>
                        <Input
                            placeholder='Search by Name, Email, or Id'
                            startIcon={search}
                            style={{ width: "100%", maxWidth: "380px" }}
                            value={searchTerm}
                            onChange={e => setSearchTerm(e.target.value)}
                        />
                    </div>
                    {isLoading ? (
                        <p className="text-center mt-4">Loading deductions...</p>
                    ) : selectedTab === 2 ? (
                        garnishmentRaw.length === 0 ? (
                            <p className="text-center text-muted mt-4">No wage garnishments on file.</p>
                        ) : (
                            <table className="table table-hover" style={{ fontSize: 13 }}>
                                <thead style={{ background: '#f8f9fa' }}>
                                    <tr>
                                        <th>Employee</th>
                                        <th>Type</th>
                                        <th>Case #</th>
                                        <th>Amount / %</th>
                                        <th>Effective</th>
                                        <th>Status</th>
                                        <th></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {garnishmentRaw.map(d => {
                                        const desc = d.description || '';
                                        const typeMatch = desc.replace('[Garnishment]', '').split('|')[0].trim();
                                        const caseMatch = desc.match(/Case #([^|]+)/)?.[1]?.trim() || '—';
                                        return (
                                            <tr key={d.id}>
                                                <td>{employeeMap[d.employeeId] || d.employeeId || '—'}</td>
                                                <td>{typeMatch}</td>
                                                <td>{caseMatch}</td>
                                                <td>{d.amount != null ? `$${d.amount}` : (d.percent != null ? `${d.percent}%` : '—')}</td>
                                                <td>{d.effectiveStart ? new Date(d.effectiveStart).toLocaleDateString() : '—'}{d.effectiveEnd ? ` – ${new Date(d.effectiveEnd).toLocaleDateString()}` : ''}</td>
                                                <td>
                                                    <span style={{ background: d.active !== false ? '#d4edda' : '#f8d7da', color: d.active !== false ? '#155724' : '#721c24', padding: '2px 10px', borderRadius: 10, fontSize: 11, fontWeight: 600 }}>
                                                        {d.active !== false ? 'Active' : 'Inactive'}
                                                    </span>
                                                </td>
                                                <td>
                                                    <button className="btn btn-sm btn-outline-secondary" onClick={() => navigate(`/deductions/add-edit-garnishment-deduction?id=${d.id}`)}>Edit</button>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        )
                    ) : (
                        <Table
                            headers={selectedTab === 0 ? mcnDeductColumns : childSupportDeductColumns}
                            data={(selectedTab === 0 ? filteredMcn : filteredChildSupport).map(d => ({
                                ...d,
                                active: (selectedTab === 0 ? miscRaw : childSupportRaw).find(r => r.id === d.id)?.active,
                            }))}
                            handleView={(d) => {
                                const raw = selectedTab === 0
                                    ? miscRaw.find(r => r.id === d.id)
                                    : childSupportRaw.find(r => r.id === d.id);
                                setViewDeduction(raw || d);
                            }}
                            handleEdit={(d) => {
                                if (selectedTab === 0) {
                                    navigate(`/deductions/add-edit-mcn-deduction-benefit?id=${d.id}`);
                                } else {
                                    navigate(`/deductions/add-edit-child-support-deduction?id=${d.id}`);
                                }
                            }}
                            handleToggleActive={handleToggleActive}
                        />
                    )}
                </div>
            </div>
        </div>
        <ConfirmModal
            isOpen={!!confirmModal}
            title={confirmModal?.title}
            message={confirmModal?.message}
            confirmLabel={confirmModal?.confirmLabel}
            confirmVariant={confirmModal?.confirmVariant}
            onConfirm={confirmModal?.onConfirm}
            onCancel={() => setConfirmModal(null)}
        />
        {viewDeduction && (
            <div className="modal show d-block" style={{ background: "rgba(0,0,0,0.5)" }} onClick={() => setViewDeduction(null)}>
                <div className="modal-dialog modal-lg" onClick={e => e.stopPropagation()}>
                    <div className="modal-content p-4">
                        <div className="d-flex justify-content-between align-items-center mb-3">
                            <h5 className="mb-0">{selectedTab === 0 ? 'Miscellaneous Deduction' : 'Child Support Deduction'} Details</h5>
                            <button className="btn-close" onClick={() => setViewDeduction(null)} />
                        </div>
                        <div className="row g-3">
                            <div className="col-6"><small className="text-muted">Employee</small><p className="fw-bold">{employeeMap[viewDeduction.employeeId] || viewDeduction.employeeId || '—'}</p></div>
                            <div className="col-6"><small className="text-muted">Description</small><p className="fw-bold">{viewDeduction.description || '—'}</p></div>
                            <div className="col-6"><small className="text-muted">Amount</small><p className="fw-bold">{viewDeduction.amount != null ? `$${viewDeduction.amount}` : '—'}</p></div>
                            <div className="col-6"><small className="text-muted">Start Date</small><p className="fw-bold">{fmtDate(viewDeduction.effectiveStart || viewDeduction.effective)}</p></div>
                            <div className="col-6"><small className="text-muted">End Date</small><p className="fw-bold">{fmtDate(viewDeduction.effectiveEnd)}</p></div>
                            {selectedTab === 0 && <>
                                <div className="col-6"><small className="text-muted">Deduction %</small><p className="fw-bold">{viewDeduction.percent != null ? `${viewDeduction.percent}%` : '—'}</p></div>
                                <div className="col-6"><small className="text-muted">Annual Limit</small><p className="fw-bold">{viewDeduction.annualLimitAmount != null ? `$${viewDeduction.annualLimitAmount}` : '—'}</p></div>
                                <div className="col-6"><small className="text-muted">Lifetime Limit</small><p className="fw-bold">{viewDeduction.totalLimitAmount != null ? `$${viewDeduction.totalLimitAmount}` : '—'}</p></div>
                            </>}
                            {selectedTab === 1 && <>
                                <div className="col-6"><small className="text-muted">Max Withholding %</small><p className="fw-bold">{viewDeduction.maxPercent != null ? `${viewDeduction.maxPercent}%` : '—'}</p></div>
                                <div className="col-6"><small className="text-muted">Case # (External ID)</small><p className="fw-bold">{viewDeduction.externalId || '—'}</p></div>
                                <div className="col-6"><small className="text-muted">FIPS Code</small><p className="fw-bold">{viewDeduction.fipsCode || '—'}</p></div>
                                <div className="col-6"><small className="text-muted">Issuing State</small><p className="fw-bold">{viewDeduction.issuingState || '—'}</p></div>
                                <div className="col-6"><small className="text-muted">Issue Date</small><p className="fw-bold">{fmtDate(viewDeduction.issueDate)}</p></div>
                                <div className="col-6"><small className="text-muted">Is Managed</small><p className="fw-bold">{viewDeduction.isManaged ? 'Yes' : 'No'}</p></div>
                            </>}
                        </div>
                    </div>
                </div>
            </div>
        )}
        </>
    );
}


export default Deductions;
